import {
  CalendarDays, Users, Smartphone, Heart, FileText, Receipt,
  MapPin, MessagesSquare, BarChart3, Banknote, Key,
} from "lucide-react";

// Single source of truth for nav / hub / SEO / routing.
// Page CONTENT lives in features/pages.jsx — this only holds metadata.
export const FEATURES = [
  {
    slug: "schema-kalender", icon: CalendarDays, group: "PLANERA & SCHEMALÄGG",
    name: "Schema & kalender", eyebrow: "Planering & kalender",
    tagline: "Planera veckan och fördela jobb till rätt person",
    seoTitle: "Schema & kalender för städföretag | Servora",
    seoDesc: "Planera dag, vecka och månad, fördela pass till rätt personal och hantera frånvaro — allt i en kalender.",
    live: true, size: "lg", plan: "basic", related: ["mobilapp", "personal-team"],
  },
  {
    slug: "personal-team", icon: Users, group: "PLANERA & SCHEMALÄGG",
    name: "Personal & team", eyebrow: "Personal & team",
    tagline: "Register, roller, behörigheter och frånvaro",
    seoTitle: "Personal & team — hantera medarbetare | Servora",
    seoDesc: "Samla medarbetare, roller, behörigheter, avtal och ledighet på ett ställe.",
    live: true, size: "md", plan: "basic", related: ["schema-kalender", "lon-ekonomi"],
  },
  {
    slug: "mobilapp", icon: Smartphone, group: "PLANERA & SCHEMALÄGG",
    name: "Mobilapp för personal", eyebrow: "App för teamet",
    tagline: "Schema, incheckning med plats, checklistor och foto",
    seoTitle: "Mobilapp för städpersonal | Servora",
    seoDesc: "Personalen ser dagens uppdrag, stämplar in med plats, kvitterar checklistor med foto och ser sin lön — i mobilen.",
    live: true, size: "lg", plan: "basic", related: ["schema-kalender", "karta-drift"],
  },
  {
    slug: "kunder-crm", icon: Heart, group: "SÄLJ & FAKTURERA",
    name: "Kunder & CRM", eyebrow: "Kunder & relationer",
    tagline: "Kundregister, historik, avtal och dokument",
    seoTitle: "Kunder & CRM för städföretag | Servora",
    seoDesc: "Håll ordning på kunder, historik, avtal och signerade dokument — kopplat till bokningar och fakturor.",
    live: true, size: "md", plan: "pro", related: ["offerter-bokningar", "kommunikation"],
  },
  {
    slug: "offerter-bokningar", icon: FileText, group: "SÄLJ & FAKTURERA",
    name: "Offerter & bokningar", eyebrow: "Offert till bokning",
    tagline: "Från offert och signering till bokat jobb",
    seoTitle: "Offerter & bokningar | Servora",
    seoDesc: "Skapa offerter med RUT, låt kunden signera digitalt och gör dem till bokningar och fakturor.",
    live: true, size: "md", plan: "pro", related: ["fakturering-rut", "kunder-crm"],
  },
  {
    slug: "fakturering-rut", icon: Receipt, group: "SÄLJ & FAKTURERA",
    name: "Fakturering & RUT", eyebrow: "Fakturering & ekonomi",
    tagline: "Fakturor, RUT-avdrag, moms och Fortnox",
    seoTitle: "Fakturering & RUT-avdrag | Servora",
    seoDesc: "Skapa fakturor med RUT-avdrag, moms och koppling till Fortnox och Skatteverket.",
    live: true, size: "lg", plan: "basic", related: ["lon-ekonomi", "offerter-bokningar"],
  },
  {
    slug: "karta-drift", icon: MapPin, group: "KOMMUNICERA & ANALYS",
    name: "Karta & drift", eyebrow: "Karta & drift",
    tagline: "Platskarta, fordon och objekt",
    seoTitle: "Karta & drift — platskarta och fordon | Servora",
    seoDesc: "Se personalens platser på kartan, håll koll på fordon och samla objekt och instruktioner.",
    live: true, size: "md", plan: "basic", related: ["mobilapp", "schema-kalender"],
  },
  {
    slug: "kommunikation", icon: MessagesSquare, group: "KOMMUNICERA & ANALYS",
    name: "Kommunikation", eyebrow: "Kommunikation",
    tagline: "SMS, e-post, mallar och anslagstavla",
    seoTitle: "Kommunikation — SMS & e-post | Servora",
    seoDesc: "Nå kunder och personal med SMS, e-postinkorg, mallar, anslagstavla och interna meddelanden.",
    live: true, size: "md", plan: "pro", related: ["kunder-crm", "mobilapp"],
  },
  {
    slug: "lon-ekonomi", icon: Banknote, group: "KOMMUNICERA & ANALYS",
    name: "Lön & ekonomi", eyebrow: "Lön & ekonomi",
    tagline: "Löneunderlag, utlägg och ekonomisk översikt",
    seoTitle: "Lön & ekonomi för städföretag | Servora",
    seoDesc: "Gör tidrapporter till löneunderlag, hantera utlägg och följ ekonomin i realtid.",
    live: true, size: "md", plan: "basic", related: ["fakturering-rut", "personal-team"],
  },
  {
    slug: "analys-sakerhet", icon: BarChart3, group: "KOMMUNICERA & ANALYS",
    name: "Analys & säkerhet", eyebrow: "Analys & säkerhet",
    tagline: "Rapporter, export, revisionslogg och arkivering",
    seoTitle: "Analys & säkerhet | Servora",
    seoDesc: "Följ verksamheten med rapporter och export, och håll data säker med behörigheter, revisionslogg och automatisk arkivering.",
    live: true, size: "md", plan: "pro", related: ["lon-ekonomi", "fakturering-rut"],
  },
  {
    slug: "nyckelhantering", icon: Key, group: "KOMMUNICERA & ANALYS",
    name: "Nyckelhantering", eyebrow: "Nyckelhantering",
    tagline: "Håll koll på vem som har vilken nyckel",
    seoTitle: "Nyckelhantering för städföretag | Servora",
    seoDesc: "Registrera nycklar med löpnummer, lämna ut och ta emot dem och se hela historiken.",
    live: true, size: "md", plan: "basic", related: ["karta-drift", "mobilapp"],
  },
];

const BY_SLUG = FEATURES.reduce((acc, f) => { acc[f.slug] = f; return acc; }, {});
export const getFeature = (slug) => BY_SLUG[slug];
export const LIVE_FEATURES = FEATURES.filter((f) => f.live);
export const LIVE_SLUGS = LIVE_FEATURES.map((f) => f.slug);

export const FEATURE_SEO = FEATURES.reduce((acc, f) => {
  acc[`/funktioner/${f.slug}`] = [f.seoTitle, f.seoDesc];
  return acc;
}, {});
